import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/hooks/use-app-context';
import { isConsentGranted } from './collection-place.contract';
import { collectionPlaceQueryOptions } from './collection-place.service';
import { clearDeclaredPlace } from './place-session-store';

const activePrefetches = new WeakMap<QueryClient, Map<string, Promise<void>>>();
const prefetchedConsentCycles = new Set<string>();

export function UserPlaceCachePrivacyBoundary() {
  const { user, language } = useApp();
  const queryClient = useQueryClient();
  const previousUserId = useRef<string | null>(null);
  const previousConsent = useRef(false);
  const userId = user?.id ?? null;
  const consentGranted = isConsentGranted(user?.geolocationConsent);
  const lang = language === 'en' ? 'en' : 'fr';

  useEffect(() => {
    const previous = previousUserId.current;
    if (previous && previous !== userId) {
      const queryKey = ['geo', 'reverse', previous];
      void queryClient.cancelQueries({ queryKey });
      queryClient.removeQueries({ queryKey });
      clearDeclaredPlace(previous);
      prefetchedConsentCycles.delete(`${previous}:fr`);
      prefetchedConsentCycles.delete(`${previous}:en`);
    }
    previousUserId.current = userId;
    if (previousConsent.current && !consentGranted && userId) {
      const queryKey = ['geo', 'reverse', userId];
      void queryClient.cancelQueries({ queryKey });
      queryClient.removeQueries({ queryKey });
      clearDeclaredPlace(userId);
      prefetchedConsentCycles.delete(`${userId}:${lang}`);
    }
    previousConsent.current = consentGranted;
  }, [consentGranted, lang, queryClient, userId]);

  useEffect(() => {
    if (!userId || !consentGranted) return;
    const cycleKey = `${userId}:${lang}`;
    let isActive = true;
    const timer = window.setTimeout(() => {
      void (async () => {
        if (!navigator.permissions) return;
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        if (!isActive || permission.state !== 'granted' || prefetchedConsentCycles.has(cycleKey)) return;
        prefetchedConsentCycles.add(cycleKey);
        await prefetchPlaceOnce(queryClient, userId, lang);
      })().catch(() => undefined);
    }, 100);

    return () => {
      isActive = false;
      window.clearTimeout(timer);
    };
  }, [consentGranted, lang, queryClient, userId]);

  return null;
}

function prefetchPlaceOnce(queryClient: QueryClient, userId: string, language: 'fr' | 'en'): Promise<void> {
  const key = `${userId}:${language}`;
  const requests = activePrefetches.get(queryClient) ?? new Map<string, Promise<void>>();
  activePrefetches.set(queryClient, requests);
  const active = requests.get(key);
  if (active) return active;
  const request = queryClient.prefetchQuery(collectionPlaceQueryOptions(userId, language)).finally(() => {
    if (requests.get(key) === request) requests.delete(key);
  });
  requests.set(key, request);
  return request;
}
